import React, { useCallback, useMemo } from 'react'
import { Avatar } from '@components/Avatar/Avatar'
import { PaginatedUsers } from '@harmony/live-types'
import { Icon, Paragraph, Stack } from '@ui'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'

import { SpeakingAvatar } from './SpeakingAvatar'

const MAX_AVATARS = 3

export interface LiveUsersPillProps {
    type: 'stage' | 'listeners'
    userIds: string[]
    totalCount: number
    speakingUserIds?: Set<string>
    cursor?: string
    onLoadMore?: () => void
    /** Stage requests to show (only for listeners pill) */
    stageRequests?: PaginatedUsers
    /** Whether the current user is on stage (can moderate) */
    isOnStage?: boolean
    /** Space ID for the live channel */
    spaceId?: string
    /** Channel ID for moderation actions */
    channelId?: string
    /** Channel name for display in panel */
    channelName?: string
    /** Current stage size (only for stage pill) */
    stageSize?: number
    /** Current user ID (to hide kick button for self) */
    currentUserId?: string
    /** Callback to moderate stage */
    onModerateStage?: (action: import('@harmony/live-types').ModerateStageAction) => void
}

export const LiveUsersPill = React.memo((props: LiveUsersPillProps) => {
    const {
        type,
        userIds,
        totalCount,
        currentUserId,
        speakingUserIds,
        stageRequests,
        isOnStage,
        spaceId,
        channelId,
        channelName,
    } = props

    const { openPanel } = usePanelActions()

    const displayUserIds = useMemo(() => {
        // Sort current user first, then take first MAX_AVATARS
        const sorted = currentUserId
            ? [...userIds].sort((a, b) => {
                  if (a === currentUserId) {
                      return -1
                  }
                  if (b === currentUserId) {
                      return 1
                  }
                  return 0
              })
            : userIds
        return sorted.slice(0, MAX_AVATARS)
    }, [userIds, currentUserId])

    const label = type === 'stage' ? 'Stage' : 'Audience'
    const iconType = type === 'stage' ? 'microphone' : 'people'

    // Display count is just the total count for that section
    const displayCount = totalCount

    // Show badge when there are stage requests (only for stage pill when user is on stage)
    const hasStageRequests = (stageRequests?.totalCount ?? 0) > 0

    const isStage = type === 'stage'
    const isClickable = !!spaceId && !!channelId

    const handleClick = useCallback(() => {
        if (spaceId && channelId) {
            openPanel('live-participants', {
                spaceId,
                channelId,
                ...(channelName ? { channelName } : {}),
            })
        }
    }, [openPanel, spaceId, channelId, channelName])

    return (
        <Stack
            horizontal
            hoverable={isClickable}
            gap="paragraph"
            rounded="full"
            height="x5"
            overflow="hidden"
            background="level2"
            paddingY="sm"
            paddingX="md"
            alignItems="center"
            color="gray1"
            cursor={isClickable ? 'pointer' : undefined}
            onClick={isClickable ? handleClick : undefined}
        >
            {isStage ? (
                <>
                    {/* Stage pill: mic icon (with dot if requests) → count + label → avatars */}
                    <Icon
                        type={hasStageRequests && isOnStage ? 'microphoneDot' : 'microphone'}
                        size="square_sm"
                    />
                    <Paragraph size="sm" fontWeight="medium">
                        {displayCount} {label}
                    </Paragraph>
                    {displayUserIds.length > 0 && (
                        <Stack horizontal gap="line">
                            {displayUserIds.map((userId) => (
                                <SpeakingAvatar
                                    key={userId}
                                    userId={userId}
                                    isSpeaking={speakingUserIds?.has(userId) ?? false}
                                />
                            ))}
                        </Stack>
                    )}
                </>
            ) : (
                <>
                    {/* Audience pill: people icon → count + label → avatars */}
                    <Icon type={iconType} size="square_sm" />
                    <Paragraph size="sm" fontWeight="medium">
                        {displayCount} {label}
                    </Paragraph>
                    {displayUserIds.length > 0 && (
                        <Stack horizontal gap="line">
                            {displayUserIds.map((userId) => (
                                <Avatar key={userId} size="avatar_sm" userId={userId} />
                            ))}
                        </Stack>
                    )}
                </>
            )}
        </Stack>
    )
})

LiveUsersPill.displayName = 'LiveUsersPill'
